import React from "react";
import { LANDING } from "@/constants/testIds";

/**
 * Portrait — renders the partner photo when available, otherwise
 * falls back to an elegant monochrome card with italic initials.
 * Photos are duo-toned (slight desaturation + warm gold lift) to
 * match the institutional aesthetic.
 */
const Portrait = ({ photo, initials, name, testId }) => (
  <div
    data-testid={testId}
    className="relative aspect-[4/5] w-full overflow-hidden border border-[var(--kr-line)] bg-[#0c1018]"
  >
    {photo ? (
      <>
        <img
          src={photo}
          alt={name}
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            filter:
              "grayscale(35%) contrast(1.05) brightness(0.95) saturate(0.9)",
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(10,13,18,0) 50%, rgba(10,13,18,0.55) 100%), radial-gradient(ellipse at 80% 10%, rgba(201,169,110,0.10), transparent 55%)",
          }}
        />
      </>
    ) : (
      <>
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 35% 25%, rgba(201,169,110,0.16), transparent 55%), linear-gradient(180deg, #11161f 0%, #0a0d12 100%)",
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(201,169,110,0.18) 1px, transparent 1px), linear-gradient(90deg, rgba(201,169,110,0.18) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
            maskImage:
              "radial-gradient(ellipse at 50% 35%, black 30%, transparent 75%)",
            WebkitMaskImage:
              "radial-gradient(ellipse at 50% 35%, black 30%, transparent 75%)",
          }}
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="font-serif-display italic text-[5.5rem] md:text-[7rem] leading-none text-[var(--kr-gold)]">
            {initials}
          </div>
          <div className="mt-6 font-mono-label text-[9px] text-[var(--kr-text-muted)]">
            Portrait · Forthcoming
          </div>
        </div>
      </>
    )}
    {/* corner brackets */}
    <span className="absolute top-3 left-3 w-5 h-5 border-l border-t border-[var(--kr-gold)]/70" />
    <span className="absolute top-3 right-3 w-5 h-5 border-r border-t border-[var(--kr-gold)]/70" />
    <span className="absolute bottom-3 left-3 w-5 h-5 border-l border-b border-[var(--kr-gold)]/70" />
    <span className="absolute bottom-3 right-3 w-5 h-5 border-r border-b border-[var(--kr-gold)]/70" />
  </div>
);

/**
 * PartnerCard — named senior partner card for the /partnership page.
 * Portrait on the left, biography + Contact / LinkedIn buttons on the right.
 */
const PartnerCard = ({ partner, index }) => {
  const reverse = index % 2 === 1;
  const firstName = partner.name.split(" ")[0];
  return (
    <article
      id={partner.anchor}
      data-testid={`${LANDING.partnershipPagePartnerPrefix}${partner.id}`}
      className="grid lg:grid-cols-12 gap-10 lg:gap-16 py-16 border-b border-[var(--kr-line-soft)] last:border-b-0 scroll-mt-32"
    >
      <div className={`lg:col-span-5 ${reverse ? "lg:order-2" : ""}`}>
        <Portrait
          photo={partner.photo}
          initials={partner.initials}
          name={partner.name}
          testId={`${LANDING.partnershipPagePortraitPrefix}${partner.id}`}
        />
        <div className="mt-6 font-mono-label text-[10px] text-[var(--kr-text-muted)]">
          Profile · {partner.id.toUpperCase()}
        </div>
      </div>

      <div
        className={`lg:col-span-7 ${reverse ? "lg:order-1 lg:pr-6" : "lg:pl-4"}`}
      >
        <div className="font-mono-label text-[10px] text-[var(--kr-gold)] mb-4">
          {partner.role}
        </div>
        <h3 className="font-serif-display text-[2.4rem] lg:text-[2.9rem] leading-[1.05] text-[var(--kr-text)] mb-4">
          {partner.name}
        </h3>
        <div className="font-serif-display italic text-[var(--kr-text-muted)] text-[1.1rem] mb-8">
          {partner.subtitle}
        </div>

        <div className="kr-rule mb-8" />

        {/* About me */}
        <div className="font-mono-label text-[10px] text-[var(--kr-text-muted)] mb-4">
          About
        </div>
        <div className="space-y-5 text-[var(--kr-text)] text-[15.5px] leading-[1.85] max-w-[64ch]">
          {partner.bio.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>

        {/* Focus pillars */}
        {partner.pillars?.length ? (
          <div className="mt-10 grid sm:grid-cols-3 gap-6">
            {partner.pillars.map((p) => (
              <div
                key={p.label}
                className="border-t border-[var(--kr-line)] pt-4"
              >
                <div className="font-mono-label text-[9px] text-[var(--kr-text-muted)] mb-2">
                  {p.label}
                </div>
                <div className="font-serif-display text-[var(--kr-text)] text-[1.05rem]">
                  {p.value}
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {/* Contact details + CTAs */}
        <div className="mt-12 pt-8 border-t border-[var(--kr-line)]">
          <div className="grid sm:grid-cols-2 gap-x-10 gap-y-6 mb-8">
            <div>
              <div className="font-mono-label text-[9px] text-[var(--kr-text-muted)] mb-2">
                Email
              </div>
              <a
                href={`mailto:${partner.email}`}
                data-testid={`${LANDING.partnerEmailPrefix}${partner.id}`}
                className="kr-link font-serif-display text-[1.05rem] text-[var(--kr-text)] break-all"
              >
                {partner.email}
              </a>
            </div>
            <div>
              <div className="font-mono-label text-[9px] text-[var(--kr-text-muted)] mb-2">
                LinkedIn
              </div>
              {partner.linkedin ? (
                <a
                  href={partner.linkedin}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`${LANDING.partnerLinkedinPrefix}${partner.id}`}
                  className="kr-link font-serif-display text-[1.05rem] text-[var(--kr-text)]"
                >
                  View Profile ↗
                </a>
              ) : (
                <span className="font-serif-display text-[1.05rem] text-[var(--kr-text-dim)]">
                  Forthcoming
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-4">
            <a
              href={`mailto:${partner.email}`}
              data-testid={`${LANDING.partnerContactBtnPrefix}${partner.id}`}
              className="kr-btn-primary"
            >
              Contact {firstName}
              <span aria-hidden>→</span>
            </a>
            {partner.linkedin ? (
              <a
                href={partner.linkedin}
                target="_blank"
                rel="noreferrer"
                data-testid={`${LANDING.partnerLinkedinBtnPrefix}${partner.id}`}
                className="kr-btn-ghost"
              >
                LinkedIn ↗
              </a>
            ) : (
              <span
                aria-disabled="true"
                data-testid={`${LANDING.partnerLinkedinBtnPrefix}${partner.id}`}
                className="kr-btn-ghost opacity-50 cursor-not-allowed"
                title="LinkedIn forthcoming"
              >
                LinkedIn · Forthcoming
              </span>
            )}
          </div>
        </div>
      </div>
    </article>
  );
};

export default PartnerCard;
